import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from '../hooks/use-toast';

export function useEntity(api, entityLabel = 'Data') {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.list();
      setItems(res.data);
    } catch (e) {
      toast({ title: `Gagal memuat ${entityLabel}`, description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [api, entityLabel]);

  useEffect(() => { load(); }, [load]);

  const create = async (data) => {
    try {
      await api.create(data);
      toast({ title: `${entityLabel} berhasil ditambahkan` });
      await load();
      return true;
    } catch (e) {
      toast({ title: 'Gagal menambah', description: e.response?.data?.detail || e.message, variant: 'destructive' });
      return false;
    }
  };

  const update = async (id, data) => {
    try {
      await api.update(id, data);
      toast({ title: `${entityLabel} berhasil diperbarui` });
      await load();
      return true;
    } catch (e) {
      toast({ title: 'Gagal memperbarui', description: e.response?.data?.detail || e.message, variant: 'destructive' });
      return false;
    }
  };

  const remove = async (id) => {
    try {
      await api.remove(id);
      toast({ title: `${entityLabel} berhasil dihapus` });
      await load();
      return true;
    } catch (e) {
      toast({ title: 'Gagal menghapus', description: e.response?.data?.detail || e.message, variant: 'destructive' });
      return false;
    }
  };

  return { items, loading, load, create, update, remove };
}

/**
 * Like useEntity, but for lists that can grow large (thousands of rows):
 * fetches one page at a time from the backend instead of the whole
 * collection, with server-side search + optional extra filters.
 * The backend returns {items, total, page, limit, pages} when `limit`
 * is passed in the query - see make_crud() in server.py.
 */
export function usePagedEntity(api, entityLabel = 'Data', { limit = 50, search = '', filters = {} } = {}) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  // Guards against a slow, now-stale request (e.g. for a page/filter that no
  // longer applies) resolving after a newer one and overwriting its result.
  const requestId = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const filtersKey = JSON.stringify(filters);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); }, [debouncedSearch, filtersKey]);

  const load = useCallback(async () => {
    const myId = ++requestId.current;
    setLoading(true);
    try {
      const res = await api.list({ page, limit, search: debouncedSearch || undefined, ...filters });
      if (myId !== requestId.current) return; // superseded by a newer request
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch (e) {
      if (myId !== requestId.current) return;
      toast({ title: `Gagal memuat ${entityLabel}`, description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      if (myId === requestId.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, entityLabel, page, limit, debouncedSearch, filtersKey]);

  useEffect(() => { load(); }, [load]);

  const create = async (data) => {
    try {
      await api.create(data);
      toast({ title: `${entityLabel} berhasil ditambahkan` });
      await load();
      return true;
    } catch (e) {
      toast({ title: 'Gagal menambah', description: e.response?.data?.detail || e.message, variant: 'destructive' });
      return false;
    }
  };

  const update = async (id, data) => {
    try {
      await api.update(id, data);
      toast({ title: `${entityLabel} berhasil diperbarui` });
      await load();
      return true;
    } catch (e) {
      toast({ title: 'Gagal memperbarui', description: e.response?.data?.detail || e.message, variant: 'destructive' });
      return false;
    }
  };

  const remove = async (id) => {
    try {
      await api.remove(id);
      toast({ title: `${entityLabel} berhasil dihapus` });
      await load();
      return true;
    } catch (e) {
      toast({ title: 'Gagal menghapus', description: e.response?.data?.detail || e.message, variant: 'destructive' });
      return false;
    }
  };

  return { items, total, page, pages, limit, loading, setPage, load, create, update, remove };
}

export function useStats() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { statsApi } = await import('../lib/api');
        const res = await statsApi.summary();
        setStats(res.data);
      } catch (e) { console.error(e); }
    })();
  }, []);
  return stats;
}
